import { useMemo } from 'react';
import styled from 'styled-components';

import { DISCHARGE_CALENDAR_ROUTES } from 'components/dischargeCalendar/constants';
import { Flags } from 'constants/flags';
import { useEnvironment } from 'context/environment';
import { useProfile } from 'context/profile';
import { ImportType } from 'models/Client';
import { PERMISSIONS, PRODUCT_ACCESSORS } from 'models/Profile';
import AllPatientsIcon from 'svg/AllPatientsIcon';
import ContractedHealthPlansIcon from 'svg/ContractedHealthPlansIcon';
import CoordinatePatientsIcon from 'svg/CoordinatePatientsIcon';
import DischargeCalendarIcon from 'svg/DischargeCalendarIcon';
import GearIcon from 'svg/GearIcon';
import InsightsIcon from 'svg/InsightsIcon';
import PatientPortfolioIcon from 'svg/PatientPortfolioIcon';
import ReferralsIcon from 'svg/ReferralIcon';
import SummarizationIcon from 'svg/SummarizationIcon';

import AccessGuard from './shared/AccessGuard';
import SideNavLink from './SideNavLink';
import { SideNavSection } from './SideNavSection';

const SideNavbar = () => {
  const { profile } = useProfile();
  const { environment } = useEnvironment();
  const hasAdminInPatient = profile.hasFlag(Flags.AdminInPatient);

  const showConnectPatients = useMemo(() => {
    return (
      profile.has(PERMISSIONS.patientCreate) &&
      profile.actingClient.importType === ImportType.IMPORTED_PATIENT &&
      profile.canActAsManager &&
      (profile.isAdmin || (!profile.isPhysicianTeamUser && !profile.isAffiliateUser))
    );
  }, [profile]);

  const showReferrals =
    !!profile.actingClient.providedProviderTypes.length ||
    !!profile.actingClient?.clientManagedGroupTypes.some((clientGroupType) => clientGroupType.config.referral);
  const hasHealthPlans = profile.actingClient.providedProviderTypes.some((x) => x.config.designatesAcceptedPlans);

  return (
    <NavContainer>
      <NavContent>
        <AccessGuard accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
          <SideNavSection title='Care Coordination'>
            <SideNavLink
              to='/patients'
              title='Patient Portfolio'
              icon={<PatientPortfolioIcon width={16} height={16} />}
            />
            <SideNavLink to='/all-patients' title='All Patients' icon={<AllPatientsIcon width={16} height={16} />} />
            <SideNavLink
              to='/coordinate'
              data-cy='coordinateLink'
              title='Coordinate'
              icon={<CoordinatePatientsIcon color={'currentColor'} width={16} height={16} />}
              visible={showConnectPatients}
            />
          </SideNavSection>
          <SideNavSection title='Census Management' visible={profile.has(PRODUCT_ACCESSORS.dischargeCalendarEnabled)}>
            <SideNavLink
              to={DISCHARGE_CALENDAR_ROUTES.BASE}
              title='Discharge Calendar'
              data-cy='discharge-calendar'
              icon={<DischargeCalendarIcon width={16} height={16} />}
              end={false}
            />
          </SideNavSection>
          <SideNavSection title='Referrals' visible={showReferrals}>
            <SideNavLink
              data-cy='referrals-link'
              to='/referrals'
              title='Active Referrals'
              icon={<ReferralsIcon width={16} height={16} />}
            />
            <SideNavLink
              data-cy='contractedHealthPlansLink'
              to='/contracted-health-plans'
              title='Contracted Health Plans'
              icon={<ContractedHealthPlansIcon width={16} height={16} />}
              visible={hasHealthPlans}
            />
          </SideNavSection>
          <SideNavSection title='Analytics'>
            <SideNavLink to='/insights' title='Olio Insights' icon={<InsightsIcon width={16} height={16} />} />
          </SideNavSection>
        </AccessGuard>
        <SideNavSection title='Workspaces' visible={profile.has(PRODUCT_ACCESSORS.olioSummarizationEnabled)}>
          <SideNavLink
            title='Summarization'
            data-cy='summarizationLink'
            icon={<SummarizationIcon width={16} height={16} />}
            to={[
              {
                to: '/summarizations/new',
                subtitle: 'New Summary',
              },
              {
                to: '/summarizations',
                subtitle: 'History',
              },
            ]}
          />
        </SideNavSection>
        <SideNavSection title='Admin' visible={profile.isAdmin}>
          <SideNavLink
            to=''
            title='Legacy'
            icon={<GearIcon width={18} height={18} />}
            domain={environment?.ADMIN_UI_DOMAIN}
          />
          <AccessGuard accessor={PERMISSIONS.adminClientView}>
            <SideNavLink to='/admin/clients' title='Clients' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminUserView}>
            <SideNavLink to='/admin/users' title='Users' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminGroupView}>
            <SideNavLink to='/admin/groups' title='Groups' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminGroupView}>
            <SideNavLink to='/admin/locations' title='Locations' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminGroupTypeView}>
            <SideNavLink to='/admin/group-types' title='Group Types' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminAttrView}>
            <SideNavLink to='/admin/attributes' title='Attributes' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminAIPromptView}>
            <SideNavLink
              visible={hasAdminInPatient}
              title='AI Prompts'
              to={[
                { subtitle: 'Embedded Prompts', to: '/admin/ai-prompts', permission: PERMISSIONS.adminAIPromptView },
                {
                  subtitle: 'Workspace Prompts',
                  to: '/admin/workspace-prompts',
                  permission: PERMISSIONS.adminAIPromptView,
                },
                {
                  subtitle: 'Generated Summaries',
                  to: '/admin/generated-summaries',
                  permission: PERMISSIONS.adminAIPromptView,
                },
              ]}
            />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminQuestionTemplateView}>
            <SideNavLink to='/admin/question-templates' title='Question Templates' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminDischargeReasonView}>
            <SideNavLink to='/admin/discharge-reasons' title='Discharge Dispositions' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminClassificationView}>
            <SideNavLink to='/admin/plans' title='Plans' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminClassificationView}>
            <SideNavLink to='/admin/plan-types' title='Plan Types' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminClassificationView}>
            <SideNavLink to='/admin/episode-types' title='Episode Types' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminFeatureFlagView}>
            <SideNavLink to='/admin/feature-flags' title='Feature Flags' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminImportedPatientView}>
            <SideNavLink to='/admin/import-history' title='Import History' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminLinkedRecordView}>
            <SideNavLink to='/admin/shared-records' title='Shared Records' visible={hasAdminInPatient} />
          </AccessGuard>
        </SideNavSection>
      </NavContent>
    </NavContainer>
  );
};

export default SideNavbar;

const NavContainer = styled.div`
  height: 100%;
  min-width: ${({ theme }) => theme.dimensions.sideNavWidth};
  background-color: ${({ theme }) => theme.colors.black};
  flex: 0;
  display: none;

  @media ${({ theme }) => theme.devices.desktop} {
    display: flex;
  }
`;

const NavContent = styled.nav`
  display: flex;
  flex: 1;
  flex-direction: column;

  @media ${({ theme }) => theme.devices.desktop} {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.white50};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }
`;
